import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { usePageTitle } from "@/contexts/PageTitleContext";
import { useTenant } from "@/contexts/TenantContext";
import { buildTenantPath } from "@/lib/tenantPath";
import { queryKeys } from "@/lib/queryClient";
import {
  useCalendarAppointments,
  usePendingSummaries,
} from "@/hooks/useAppointments";

import AppointmentSummaryDialog from "@/components/appointments/AppointmentSummaryDialog";
import SetAppointmentDialog from "@/components/appointments/SetAppointmentDialog";

import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

import MiniCalendar, { CalendarHeader } from "@/components/appointments/MiniCalendar";
import StartRunScriptDialog from "@/components/scripts/StartRunScriptDialog";

import { ChevronDown, Search, ClipboardList, CalendarDays, FileText, Clock } from "lucide-react";
import { CollapsibleSearch } from "@/components/ui/collapsible-search";

export default function CalendarPage() {
  usePageTitle("Calendar");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  const { selectedTenantId } = useTenant();
  
  const buildPath = useCallback((basePath) => {
    return buildTenantPath(basePath, selectedTenantId);
  }, [selectedTenantId]);

  // calendar view default
  const [calView, setCalView] = useState("agenda");
  const [calDate, setCalDate] = useState(() => new Date());

  // Start Run script picker (Agenda)
  const [scriptPickerOpen, setScriptPickerOpen] = useState(false);
  const [startRunAppt, setStartRunAppt] = useState(null);

  // Pending summaries pagination
  const [pendingOffset, setPendingOffset] = useState(0);
  const pendingLimit = 5;

  const [pendingQ, setPendingQ] = useState("");
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [summaryAppt, setSummaryAppt] = useState(null);
  
  // Set Appointment dialog state (triggered after Summary submission)
  const [setApptOpen, setSetApptOpen] = useState(false);
  const [setApptLead, setSetApptLead] = useState(null);

  const calTitle = useMemo(() => {
    const d = new Date(calDate);

    if (calView === "month") {
      return d.toLocaleString(undefined, { month: "long", year: "numeric" });
    }

    if (calView === "week") {
      const x = new Date(d);
      x.setHours(0, 0, 0, 0);
      x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); // monday
      const end = new Date(x);
      end.setDate(end.getDate() + 6);

      const startLabel = x.toLocaleString(undefined, { month: "long", day: "numeric", year: "numeric" });
      const endLabel = end.toLocaleString(undefined, { month: "long", day: "numeric", year: "numeric" });
      return `${startLabel} – ${endLabel}`;
    }

    if (calView === "agenda") {
      const x = new Date(d);
      x.setHours(0, 0, 0, 0);
      const end = new Date(x);
      end.setDate(end.getDate() + 2);

      const startLabel = x.toLocaleString(undefined, { month: "long", day: "numeric", year: "numeric" });
      const endLabel = end.toLocaleString(undefined, { month: "long", day: "numeric", year: "numeric" });
      return `${startLabel} – ${endLabel}`;
    }

    // day
    return d.toLocaleString(undefined, { month: "long", day: "numeric", year: "numeric" });
  }, [calDate, calView]);

  const shiftCalDate = (dir) => {
    const d = new Date(calDate);

    if (calView === "month") {
      const day = d.getDate();
      d.setDate(1);
      d.setMonth(d.getMonth() + dir);
      const maxDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
      d.setDate(Math.min(day, maxDay));
    } else if (calView === "week") {
      d.setDate(d.getDate() + dir * 7);
    } else if (calView === "agenda") {
      d.setDate(d.getDate() + dir * 3);
    } else {
      d.setDate(d.getDate() + dir);
    }

    setCalDate(d);
  };

  // ===== REACT QUERY HOOKS =====
  
  // Calendar appointments query - for the calendar view
  const calendarQuery = useCalendarAppointments({ enabled: true });
  
  // Pending summaries query
  const pendingSummariesQuery = usePendingSummaries({ limit: pendingLimit, offset: pendingOffset });

  // Derive calendar events from calendar query
  const calEvents = useMemo(() => {
    return calendarQuery.data || [];
  }, [calendarQuery.data]);

  // Derive pending items from query
  const pendingItems = pendingSummariesQuery.data?.items || [];
  const pendingTotal = pendingSummariesQuery.data?.total || 0;
  const pendingLoading = pendingSummariesQuery.isLoading;

  // Function to invalidate appointments queries (for refetch after mutations)
  const invalidateAppointments = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.appointments.all(selectedTenantId),
    });
    queryClient.invalidateQueries({
      queryKey: queryKeys.appointments.pendingSummaries(selectedTenantId),
    });
  }, [queryClient, selectedTenantId]);

  // Filter pending items by search
  const filteredPending = useMemo(() => {
    if (!pendingQ) return pendingItems;
    const lower = pendingQ.toLowerCase();
    return pendingItems.filter((a) => {
      const name = a.lead_snapshot?.full_name || a.title || "";
      return name.toLowerCase().includes(lower);
    });
  }, [pendingItems, pendingQ]);

  // Calculate today's appointments count
  const todayAppointmentsCount = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    return (calEvents || []).filter((e) => {
      const start = new Date(e.start_at);
      return start >= today && start < tomorrow;
    }).length;
  }, [calEvents]);

  return (
    <div className="space-y-6" data-testid="calendar-page">
      {/* Quick Stats Summary */}
      <div className="grid grid-cols-2 gap-4" data-testid="calendar-quick-stats">
        <Card className="glass">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-500/20 ring-1 ring-indigo-400/20">
                <CalendarDays className="h-5 w-5 text-indigo-400" />
              </div>
              <div>
                <div className="text-2xl font-bold text-slate-100">{todayAppointmentsCount}</div>
                <div className="text-sm text-slate-200/60">Today's Appointments</div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="glass">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-500/20 ring-1 ring-orange-400/20">
                <FileText className="h-5 w-5 text-orange-400" />
              </div>
              <div>
                <div className="text-2xl font-bold text-slate-100">{pendingTotal}</div>
                <div className="text-sm text-slate-200/60">Pending Summaries</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Calendar */}
      <Card className="glass">
        <CardContent className="p-0">
          {/* Row 1: label */}
          <div className="px-4 pt-3">
            <div className="flex items-center gap-2 text-lg font-semibold text-slate-100">
              <CalendarDays className="h-5 w-5 text-indigo-400" />
              Calendar
            </div>
          </div>

          {/* Row 2: month/year + view + prev/next */}
          <div className="flex items-center justify-between gap-3 px-4 py-3">
            <CalendarHeader title={calTitle} className="text-sm font-semibold text-slate-100" />

            <div className="flex items-center gap-2">
              {(() => {
                const t = new Date();
                t.setHours(0, 0, 0, 0);
                const c = new Date(calDate);
                c.setHours(0, 0, 0, 0);
                const showToday = t.getTime() !== c.getTime();

                return showToday ? (
                  <Button
                    type="button"
                    variant="secondary"
                    className="h-9 rounded-full bg-white/10 text-slate-100 ring-1 ring-white/15 hover:bg-white/15"
                    onClick={() => setCalDate(new Date())}
                  >
                    Today
                  </Button>
                ) : null;
              })()}

              <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="secondary"
                  className="h-9 rounded-full bg-indigo-500/20 text-indigo-100 ring-1 ring-indigo-400/20 hover:bg-indigo-500/30"
                  data-testid="calendar-view-trigger"
                >
                  {calView === "agenda" ? "Agenda" : calView === "month" ? "Month" : calView === "week" ? "Week" : "Day"}
                  <ChevronDown className="ml-2 h-4 w-4 opacity-80" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="glass border-white/10 text-slate-100" align="end">
                <DropdownMenuItem onClick={() => setCalView("agenda")} className="text-slate-100 focus:bg-white/10 focus:text-slate-100">
                  Agenda
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setCalView("month")} className="text-slate-100 focus:bg-white/10 focus:text-slate-100">
                  Month
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setCalView("week")} className="text-slate-100 focus:bg-white/10 focus:text-slate-100">
                  Week
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setCalView("day")} className="text-slate-100 focus:bg-white/10 focus:text-slate-100">
                  Day
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

              <Button
                type="button"
                variant="secondary"
                className="h-9 w-9 rounded-xl bg-white/10 text-slate-100 ring-1 ring-white/15 hover:bg-white/15"
                aria-label="Previous"
                onClick={() => shiftCalDate(-1)}
              >
                <span className="sr-only">Previous</span>
                <span aria-hidden="true">‹</span>
              </Button>
              <Button
                type="button"
                variant="secondary"
                className="h-9 w-9 rounded-xl bg-white/10 text-slate-100 ring-1 ring-white/15 hover:bg-white/15"
                aria-label="Next"
                onClick={() => shiftCalDate(1)}
              >
                <span className="sr-only">Next</span>
                <span aria-hidden="true">›</span>
              </Button>
            </div>
          </div>

          {/* Calendar body */}
          <div className="border-t border-white/10 px-0 py-0">
            <MiniCalendar
              date={calDate}
              view={calView}
              events={calEvents}
              onDateChange={setCalDate}
              onEventClick={(appt) => navigate(buildPath(`/app/appointments/${appt.id}`))}
              onStartRun={(appt) => {
                setStartRunAppt(appt);
                setScriptPickerOpen(true);
              }}
            />
          </div>
        </CardContent>
      </Card>

      {/* Pending Summaries card */}
      <Card className="glass" data-testid="pending-summaries-card">
        <CardContent className="p-0">
          {/* Header section with padding */}
          <div className="flex items-center justify-between gap-2 px-4 pt-4 pb-3">
            <div className="flex items-center gap-2 text-lg font-semibold text-slate-100">
              <FileText className="h-5 w-5 text-indigo-400" />
              Pending Summaries
            </div>

            <div className="flex-shrink-0">
              <CollapsibleSearch
                value={pendingQ}
                onChange={setPendingQ}
                placeholder="Search"
                width="w-[200px]"
                data-testid="pending-summaries-search"
              />
            </div>
          </div>

          {/* Column Headers - Always visible */}
          <div className="grid grid-cols-5 items-center gap-0 px-4 py-2 text-xs font-medium text-slate-200/50 uppercase tracking-wider">
            <div>Name</div>
            <div>Date</div>
            <div>Phone</div>
            <div>State</div>
            <div className="text-right">Summary Status</div>
          </div>

          {/* Divider below column headers - edge to edge */}
          <div className="border-t border-white/10" />

          {/* Content section with padding */}
          <div className="px-4 py-3">
            {pendingLoading ? (
              <div className="text-sm text-slate-200/60 text-center py-4">Loading...</div>
            ) : filteredPending.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <FileText className="h-12 w-12 text-indigo-400 mb-3" />
                <div className="text-sm text-slate-200/70">No pending summaries</div>
              </div>
            ) : (
              <div className="space-y-2">
                  {filteredPending.map((a) => (
                    <button
                      key={a.id}
                      type="button"
                      className="w-full rounded-xl bg-white/5 px-4 py-3 text-left ring-1 ring-white/10 hover:bg-white/10"
                      onClick={() => {
                        setSummaryAppt(a);
                        setSummaryOpen(true);
                      }}
                    >
                      <div className="grid grid-cols-5 items-center gap-0">
                        <div className="min-w-0 truncate text-sm font-semibold text-slate-100">{a.lead_snapshot?.full_name || a.title || "Appointment"}</div>
                        <div className="min-w-0 truncate text-sm text-slate-200/70">
                          {a.start_at
                            ? new Date(a.start_at).toLocaleString(undefined, {
                                year: "numeric",
                                month: "numeric",
                                day: "numeric",
                                hour: "numeric",
                                minute: "2-digit",
                              })
                            : ""}
                        </div>
                        <div className="min-w-0 truncate text-sm text-slate-200/70">{a.lead_snapshot?.phone || a.lead_snapshot?.phone_number || "—"}</div>
                        <div className="min-w-0 truncate text-sm text-slate-200/70">{a.lead_snapshot?.state || "—"}</div>
                        <div className="min-w-0 text-right">
                          <span className="inline-flex rounded-full px-3 py-1 text-xs font-medium ring-1 bg-orange-500/20 text-orange-100 ring-orange-400/25">
                            Pending
                          </span>
                        </div>
                      </div>
                    </button>
                  ))}
              </div>
            )}
          </div>

          {/* Divider above footer - edge to edge */}
          <div className="border-t border-white/10" />

          {/* Footer with pagination controls - more spacing */}
          <div className="flex items-center justify-between px-4 py-4">
            <div className="text-xs text-slate-200/60">Total pending: {pendingTotal}</div>
            {/* Only show pagination buttons when needed */}
            {pendingTotal > pendingLimit && (
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  className="h-8 rounded-full bg-white/10 text-slate-100 ring-1 ring-white/10 hover:bg-white/15"
                  disabled={pendingOffset === 0}
                  onClick={() => setPendingOffset((o) => Math.max(0, o - pendingLimit))}
                >
                  Prev
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  className="h-8 rounded-full bg-white/10 text-slate-100 ring-1 ring-white/10 hover:bg-white/15"
                  disabled={pendingOffset + pendingLimit >= pendingTotal}
                  onClick={() => setPendingOffset((o) => o + pendingLimit)}
                >
                  Next
                </Button>
              </div>
            )}
          </div>

        </CardContent>
      </Card>

      <AppointmentSummaryDialog
        open={summaryOpen}
        onOpenChange={setSummaryOpen}
        appointment={summaryAppt}
        onScheduleAfterSubmit={() => {
          if (summaryAppt?.lead_snapshot) {
            setSetApptLead(summaryAppt.lead_snapshot);
            setSetApptOpen(true);
          }
        }}
        onSubmitted={invalidateAppointments}
      />
      
      <SetAppointmentDialog
        open={setApptOpen}
        onOpenChange={setSetApptOpen}
        lead={setApptLead}
        onCreated={invalidateAppointments}
      />

      <StartRunScriptDialog
        open={scriptPickerOpen}
        onOpenChange={setScriptPickerOpen}
        appointment={startRunAppt}
      />
    </div>
  );
}
